import { ref, computed, onMounted, unref, type Ref } from "vue";
import { useApi } from "@/composables/useAppGlobals";
import { RBCDashboardResponse } from "@/types/api";
import { RBCGroupedCategory } from "@/types/rbc";
import { groupDashboardData } from "@/utils/progressUtils";

/**
 * Composable for managing RBC Dashboard state and data fetching.
 * Handles grouping logic and progress calculations.
 * 
 * @param projectId - Optional project ID (string or Ref). If not provided, it attempts to resolve it from the app context.
 */
export function useRBCDashboard(projectId?: string | Ref<string | undefined>) {
  const api = useApi();
  
  const data = ref<RBCDashboardResponse | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  /**
   * Fetches the latest dashboard snapshot from the API.
   */
  const fetchDashboard = async () => {
    // Resolve project ID: Prefer explicitly passed ID (unref handles Ref or plain string), fall back to global app context
    const activeId = unref(projectId) || api.utils.getProjectId();
    
    if (!activeId) {
      console.warn("[useRBCDashboard] No project ID available for fetch");
      return;
    }

    loading.value = true;
    try {
      data.value = await api.getRBCDashboard(activeId);
      error.value = null;
    } catch (err: unknown) {
      // Extract user-friendly error message from API response if possible
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      error.value = e.response?.data?.detail || e.message || "Failed to load dashboard data";
      console.error("[useRBCDashboard] Fetch failed:", err);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Grouped and hierarchical statistics for display in the dashboard UI.
   */
  const groupedData = computed<RBCGroupedCategory[]>(() => {
    if (!data.value) return [];
    return groupDashboardData(data.value);
  });

  /**
   * Overall project progress as a percentage string (e.g., "75.50").
   */
  const overallProgress = computed(() => {
    const val = data.value?.progress || 0;
    return (val * 100).toFixed(2);
  });

  /**
   * Number of categories that have reached the target bbox count.
   */
  const completedCount = computed(() => data.value?.completed_categories_count || 0);

  /**
   * Total number of categories being tracked for progress.
   */
  const totalCategories = computed(() => data.value?.total_target_categories || 0);

  // Initial fetch and reactivity for projectId changes
  onMounted(() => {
    fetchDashboard();
  });

  return {
    // State
    data,
    loading,
    error,
    
    // Computed Metrics
    groupedData,
    overallProgress,
    completedCount,
    totalCategories,
    
    // Actions
    fetchDashboard,
  };
}
